<template>
  <section class="zapier-manager">
    <div class="panel-head">
      <div>
        <div class="panel-title">{{ title }}</div>
        <div class="panel-subtitle">
          Connect Zapier to Loyagram through OAuth, then use the token and
          profile endpoints in your Zapier app configuration.
        </div>
      </div>
      <v-btn outline small @click="resetDefaults">Reset</v-btn>
    </div>

    <div class="zapier-grid">
      <div class="zapier-form">
        <div class="field-row">
          <div class="field">
            <label>Client ID</label>
            <v-text-field
              v-model="form.client_id"
              solo
              flat
              placeholder="Zapier OAuth client id"
            ></v-text-field>
          </div>
          <div class="field">
            <label>Redirect URI</label>
            <v-text-field
              v-model="form.redirect_uri"
              solo
              flat
              placeholder="https://zapier.com/dashboard/auth/oauth/return/App123CLIAPI/"
            ></v-text-field>
          </div>
        </div>

        <div class="field-row compact">
          <div class="field">
            <label>Scope</label>
            <v-text-field
              v-model="form.scope"
              solo
              flat
              placeholder="read"
            ></v-text-field>
          </div>
          <div class="field">
            <label>State</label>
            <v-text-field
              v-model="form.state"
              solo
              flat
              placeholder="Optional state value"
            ></v-text-field>
          </div>
        </div>

        <div class="actions">
          <v-btn
            color="primary"
            depressed
            :disabled="!canLaunch"
            @click="openAuthorize"
          >
            Open Zapier OAuth
          </v-btn>
          <v-btn outline @click="copyAuthorizeUrl">Copy authorize URL</v-btn>
        </div>
      </div>

      <div class="zapier-endpoints">
        <div class="endpoint-card">
          <div class="endpoint-title">Authorize URL</div>
          <div class="endpoint-copy">
            Use this in the Zapier OAuth authorize step.
          </div>
          <code>{{
            authorizeUrl || 'Complete the fields to generate the URL.'
          }}</code>
        </div>
        <div class="endpoint-card">
          <div class="endpoint-title">Access token URL</div>
          <div class="endpoint-copy">
            Configure this as the Zapier token exchange endpoint.
          </div>
          <code>{{ tokenUrl }}</code>
        </div>
        <div class="endpoint-card">
          <div class="endpoint-title">Test / me URL</div>
          <div class="endpoint-copy">
            Use this for Zapier account verification with bearer auth.
          </div>
          <code>{{ meUrl }}</code>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const STORAGE_KEY = 'loyagram.zapier.oauth'

function trimSlashes(value) {
  return (value || '').replace(/\/+$/, '')
}

function joinUrl(base, path) {
  if (!base) return path
  return `${trimSlashes(base)}${path}`
}

export default {
  name: 'ZapierManager',
  props: {
    title: {
      type: String,
      default: 'Zapier automation'
    }
  },
  data() {
    return {
      form: {
        client_id: '',
        redirect_uri: '',
        scope: 'read',
        state: ''
      }
    }
  },
  computed: {
    backendBase() {
      return trimSlashes(process.env.VUE_APP_API_BASE || window.location.origin)
    },
    authorizeUrl() {
      if (!this.canLaunch) return ''
      const params = new URLSearchParams({
        client_id: this.form.client_id.trim(),
        redirect_uri: this.form.redirect_uri.trim(),
        response_type: 'code'
      })
      if (this.form.scope.trim()) {
        params.set('scope', this.form.scope.trim())
      }
      if (this.form.state.trim()) {
        params.set('state', this.form.state.trim())
      }
      return `${joinUrl(
        this.backendBase,
        '/oauth/authorize'
      )}?${params.toString()}`
    },
    tokenUrl() {
      return joinUrl(this.backendBase, '/oauth/token')
    },
    meUrl() {
      return joinUrl(this.backendBase, '/oauth/me')
    },
    canLaunch() {
      return Boolean(
        this.form.client_id.trim() && this.form.redirect_uri.trim()
      )
    }
  },
  created() {
    this.loadSavedForm()
  },
  watch: {
    form: {
      deep: true,
      handler() {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.form))
      }
    }
  },
  methods: {
    loadSavedForm() {
      try {
        const saved = JSON.parse(
          window.localStorage.getItem(STORAGE_KEY) || '{}'
        )
        this.form = {
          ...this.form,
          ...saved
        }
      } catch (e) {}
    },
    resetDefaults() {
      this.form = {
        client_id: '',
        redirect_uri: '',
        scope: 'read',
        state: ''
      }
      window.localStorage.removeItem(STORAGE_KEY)
    },
    openAuthorize() {
      if (!this.authorizeUrl) return
      window.open(this.authorizeUrl, '_blank')
    },
    copyAuthorizeUrl() {
      if (!this.authorizeUrl || !navigator.clipboard) return
      navigator.clipboard.writeText(this.authorizeUrl)
    }
  }
}
</script>

<style lang="stylus" scoped>
.zapier-manager
  margin-top 32px
  padding 24px
  border 1px solid #dde6f2
  border-radius 18px
  background linear-gradient(180deg, #fbfcfe 0%, #f5f8fc 100%)

.panel-head
  display flex
  justify-content space-between
  gap 16px
  align-items flex-start
  margin-bottom 20px

.panel-title
  font-size 24px
  font-weight 700
  color #26344f

.panel-subtitle
  margin-top 6px
  max-width 760px
  color #667892
  line-height 1.6

.zapier-grid
  display grid
  grid-template-columns minmax(0, 1.2fr) minmax(320px, 0.8fr)
  gap 20px

.field-row
  display grid
  grid-template-columns repeat(2, minmax(0, 1fr))
  gap 16px
  margin-bottom 12px

.field
  label
    display block
    margin-bottom 8px
    font-size 12px
    font-weight 700
    letter-spacing 0.08em
    text-transform uppercase
    color #7586a0

.actions
  display flex
  gap 12px
  margin-top 16px

.zapier-endpoints
  display grid
  gap 12px

.endpoint-card
  padding 16px
  border 1px solid #dde6f2
  border-radius 14px
  background #fff

  code
    display block
    margin-top 10px
    padding 12px
    word-break break-word
    white-space pre-wrap
    border-radius 10px
    background #f5f8fc
    color #294273

.endpoint-title
  font-size 16px
  font-weight 700
  color #26344f

.endpoint-copy
  margin-top 4px
  color #667892
  line-height 1.5

@media (max-width: 960px)
  .zapier-grid
    grid-template-columns 1fr

  .field-row
    grid-template-columns 1fr
</style>
